import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { confirmMessage } from './message_controller';

export default class extends Controller {
  static values = {
    dirty: { type: Boolean, default: false },
    message: {
      type: String,
      default: 'You have unsaved changes. Are you sure you want to leave this page?',
    },
  };

  connect() {
    this.form = this.element.closest('form');
    this.skipNextWarning = false;

    this.boundBeforeUnload = this.beforeUnload.bind(this);
    this.boundTurboBeforeVisit = this.beforeTurboVisit.bind(this);
    this.boundTurboClick = this.handleTurboClick.bind(this);
    this.boundMarkDirty = this.markDirty.bind(this);
    this.boundSubmit = this.handleSubmit.bind(this);
    this.boundSafeClick = this.handleSafeClick.bind(this);

    window.addEventListener('beforeunload', this.boundBeforeUnload);
    document.addEventListener('turbo:before-visit', this.boundTurboBeforeVisit);
    document.addEventListener('turbo:click', this.boundTurboClick);

    this.element.addEventListener('input', this.boundMarkDirty);
    this.element.addEventListener('change', this.boundMarkDirty);
    this.element.addEventListener('click', this.boundSafeClick, true);

    if (this.form) {
      this.form.addEventListener('submit', this.boundSubmit);
    }

    this.captureInitialState();
    this.observeDomChanges();
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    document.removeEventListener('turbo:before-visit', this.boundTurboBeforeVisit);
    document.removeEventListener('turbo:click', this.boundTurboClick);

    this.element.removeEventListener('input', this.boundMarkDirty);
    this.element.removeEventListener('change', this.boundMarkDirty);
    this.element.removeEventListener('click', this.boundSafeClick, true);

    if (this.form) {
      this.form.removeEventListener('submit', this.boundSubmit);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleSafeClick(event) {
    const trigger = event.target.closest("[data-skip-dirty-warning='true']");
    if (!trigger) return;

    this.skipNextWarning = true;

    window.setTimeout(() => {
      this.skipNextWarning = false;
    }, 1000);
  }

  // turbo:before-visit's event detail only carries the destination url, not the visit's
  // action (advance/replace) - so a link's data-turbo-action can only be recovered here,
  // from the click that's about to trigger it. Cleared as soon as beforeTurboVisit reads
  // it, so a later, click-less visit (e.g. browser back/forward) never reuses a stale value.
  handleTurboClick(event) {
    const link = event.target.closest('a[data-turbo-action]');
    this.pendingVisitAction = link?.dataset.turboAction;
  }

  captureInitialState() {
    this.initialSnapshot = this.serializeInputs();
    this.dirtyValue = false;
    this.announceState();
  }

  markDirty() {
    this.dirtyValue = this.serializeInputs() !== this.initialSnapshot;
    this.announceState();
  }

  announceState() {
    this.element.dispatchEvent(
      new CustomEvent('dirty-form:changed', {
        bubbles: true,
        detail: { dirty: this.dirtyValue },
      })
    );
  }

  handleSubmit() {
    this.dirtyValue = false;
    this.announceState();
  }

  beforeUnload(event) {
    if (!this.dirtyValue || this.skipNextWarning) return;

    event.preventDefault();
    event.returnValue = this.messageValue;
    return this.messageValue;
  }

  beforeTurboVisit(event) {
    // skipNextWarning is single-use: consume it (and reset immediately) the moment it's
    // seen, rather than leaving it true indefinitely. Otherwise, if this replayed visit
    // gets cancelled elsewhere (another turbo:before-visit listener), or the controller
    // survives the visit (e.g. Turbo morph), every later navigation would silently skip
    // the dirty-form check too.
    if (this.skipNextWarning) {
      this.skipNextWarning = false;
      return;
    }

    // Same single-use handling for the action captured from handleTurboClick - a value
    // meant only for the visit that's about to fire.
    const action = this.pendingVisitAction;
    this.pendingVisitAction = undefined;

    if (!this.dirtyValue) return;

    // confirmMessage() is async and turbo:before-visit can only be cancelled
    // synchronously, so we always prevent the visit first and - if the user
    // confirms - replay it ourselves, marking it to skip this same check and passing
    // along the original visit's action so replace/advance survives the replay.
    event.preventDefault();

    const { url } = event.detail;
    confirmMessage(this.messageValue).then((confirmed) => {
      if (!confirmed) return;

      this.skipNextWarning = true;
      Turbo.visit(url, action ? { action } : {});
    });
  }

  observeDomChanges() {
    this.observer = new MutationObserver((mutations) => {
      const relevantChange = mutations.some((mutation) => {
        if (mutation.type === 'childList') return true;
        if (mutation.type === 'attributes') {
          return ['name', 'value', 'checked', 'disabled'].includes(mutation.attributeName);
        }
        return false;
      });

      if (relevantChange) {
        this.markDirty();
      }
    });

    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['name', 'value', 'checked', 'disabled'],
    });
  }

  serializeInputs() {
    const fields = Array.from(this.element.querySelectorAll('input, select, textarea'));

    const pairs = fields
      .filter((field) => field.name && !field.disabled)
      .flatMap((field) => {
        if (field.type === 'checkbox') {
          return [[field.name, field.checked ? '1' : '0']];
        }

        if (field.type === 'radio') {
          return field.checked ? [[field.name, field.value]] : [];
        }

        if (field.type === 'file') {
          const fileNames = Array.from(field.files || [])
            .map((file) => file.name)
            .join(',');
          return [[field.name, fileNames]];
        }

        return [[field.name, field.value]];
      });

    return JSON.stringify(
      pairs.sort(([aKey, aVal], [bKey, bVal]) => {
        if (aKey === bKey) return String(aVal).localeCompare(String(bVal));
        return aKey.localeCompare(bKey);
      })
    );
  }
}
